/**
 * NarrativePanel — AI Game Master narrative chat panel.
 *
 * After a stretch resolves, this panel:
 * 1. Auto-requests narration of what happened from the AI GM
 * 2. Displays streaming GM narration
 * 3. Lets the player interact via ASK/SAY/DO/OMEN action modes
 * 4. Provides a "Next Stretch" button to continue
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare,
  HelpCircle,
  MessagesSquare,
  Sword,
  Sparkles,
  Send,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { cn, uuid } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import type { StretchResolution } from '@omen-quest/stretch-engine';

// ============================================================================
// TYPES
// ============================================================================

type ActionType = 'ask' | 'say' | 'do' | 'omen';

interface NarrativeMessage {
  id: string;
  role: 'gm' | 'player';
  content: string;
  actionType?: ActionType;
}

interface ApiMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface NarrativePanelProps {
  gameId: string;
  character: {
    name: string;
    race: string;
    kitType: string;
    stats: { brawn: number; grace: number; wits: number; spirit: number };
    vitals: number;
    vitalsMax: number;
    resolve: number;
    resolveMax: number;
    equipment: string[];
  };
  game: {
    region: string;
    adventureTheme: string;
    language: string;
  };
  stretchResults: StretchResolution | null;
  dungeonName: string;
  visibleEntities?: Array<{ name: string; type: string }>;
  playerPosition?: { x: number; y: number };
  onNextStretch: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ACTION_TYPES: Array<{
  type: ActionType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  placeholder: string;
  color: string;
  bgColor: string;
}> = [
  {
    type: 'ask',
    label: 'ASK',
    icon: HelpCircle,
    placeholder: 'What do you want to know?',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/15 border-cyan-500/30',
  },
  {
    type: 'say',
    label: 'SAY',
    icon: MessagesSquare,
    placeholder: 'What do you say?',
    color: 'text-green-400',
    bgColor: 'bg-green-500/15 border-green-500/30',
  },
  {
    type: 'do',
    label: 'DO',
    icon: Sword,
    placeholder: 'What do you do?',
    color: 'text-red-400',
    bgColor: 'bg-red-500/15 border-red-500/30',
  },
  {
    type: 'omen',
    label: 'OMEN',
    icon: Sparkles,
    placeholder: 'What do you wish for?',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/15 border-purple-500/30',
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function NarrativePanel({
  gameId,
  character,
  game,
  stretchResults,
  dungeonName,
  visibleEntities,
  playerPosition,
  onNextStretch,
}: NarrativePanelProps) {
  const [messages, setMessages] = useState<NarrativeMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [input, setInput] = useState('');
  const [actionType, setActionType] = useState<ActionType>('do');
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasNarratedRef = useRef(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Build context for API calls
  const buildContext = useCallback((type: 'narrate' | 'action', action?: ActionType) => ({
    type,
    actionType: action,
    stretchResults: stretchResults ? {
      stretchNumber: stretchResults.stretchNumber,
      activityResults: stretchResults.activityResults.map(ar => ({
        activityType: ar.activityType,
        tilesRevealed: ar.tilesRevealed,
        itemsFound: ar.itemsFound,
        notes: ar.notes,
      })),
      wanderingMonster: {
        checked: stretchResults.wanderingMonster.checked,
        triggered: stretchResults.wanderingMonster.triggered,
      },
      lightSourcesDepleted: stretchResults.lightSourcesDepleted.map(l => ({ type: l.type })),
    } : undefined,
    character: {
      name: character.name,
      race: character.race,
      kitType: character.kitType,
      stats: character.stats,
      vitals: character.vitals,
      vitalsMax: character.vitalsMax,
      resolve: character.resolve,
      resolveMax: character.resolveMax,
      equipment: character.equipment,
    },
    game: {
      region: game.region,
      adventureTheme: game.adventureTheme,
      language: game.language,
    },
    dungeonName,
    visibleEntities,
    playerPosition,
  }), [stretchResults, character, game, dungeonName, visibleEntities, playerPosition]);

  // Stream a response from the server
  const streamResponse = useCallback(async (
    apiMessages: ApiMessage[],
    context: ReturnType<typeof buildContext>,
  ) => {
    const gmId = uuid();
    setMessages(prev => [...prev, { id: gmId, role: 'gm', content: '' }]);
    setIsStreaming(true);
    setError(null);

    try {
      const res = await apiFetch(`/api/chat/${gameId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages, context }),
      });

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages(prev => prev.map(m =>
          m.id === gmId ? { ...m, content: m.content + chunk } : m
        ));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection error';
      setError(msg);
      setMessages(prev => prev.map(m =>
        m.id === gmId
          ? { ...m, content: m.content || '*The Game Master falls silent...*' }
          : m
      ));
    }

    setIsStreaming(false);
  }, [gameId]);

  // Convert current messages to API format
  const toApiMessages = useCallback((msgs: NarrativeMessage[]): ApiMessage[] => {
    return msgs
      .filter(m => m.content) // skip empty
      .map(m => ({
        role: (m.role === 'gm' ? 'assistant' : 'user') as 'user' | 'assistant',
        content: m.role === 'player' && m.actionType
          ? `[${m.actionType.toUpperCase()}] ${m.content}`
          : m.content,
      }));
  }, []);

  // Auto-narrate stretch results on mount
  useEffect(() => {
    if (hasNarratedRef.current || !stretchResults) return;
    hasNarratedRef.current = true;

    // Build a narration request message
    const activity = stretchResults.activityResults[0];
    const narrationPrompt = activity
      ? `Narrate what happened as I ${activity.activityType.replace(/_/g, ' ')} during this stretch of dungeon exploration. Set the scene for what I see now.`
      : 'Narrate the current moment. What do I see around me in the dungeon?';

    const apiMessages: ApiMessage[] = [
      { role: 'user', content: narrationPrompt },
    ];

    streamResponse(apiMessages, buildContext('narrate'));
  }, [stretchResults, streamResponse, buildContext]);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle sending a player message
  const handleSend = useCallback(async () => {
    const content = input.trim();
    if (!content || isStreaming) return;

    setInput('');
    const playerMsg: NarrativeMessage = {
      id: uuid(),
      role: 'player',
      content,
      actionType,
    };

    const updatedMessages = [...messagesRef.current, playerMsg];
    setMessages(updatedMessages);

    const apiMessages = toApiMessages(updatedMessages);
    await streamResponse(apiMessages, buildContext('action', actionType));

    // Refocus input after response
    inputRef.current?.focus();
  }, [input, isStreaming, actionType, toApiMessages, streamResponse, buildContext]);

  // Handle Enter key in input
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const activeAction = ACTION_TYPES.find(a => a.type === actionType)!;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-xs font-semibold text-neutral-300 uppercase tracking-wide">
          Game Master
        </span>
        {isStreaming && (
          <Loader2 className="w-3 h-3 text-amber-400 animate-spin ml-auto" />
        )}
      </div>

      {/* ── Messages ── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0"
      >
        {messages.map((msg, idx) => (
          <div key={msg.id}>
            {msg.role === 'gm' ? (
              <div>
                {/* Show GM label only for first message or after a player message */}
                {(idx === 0 || messages[idx - 1]?.role === 'player') && (
                  <div className="text-[10px] font-semibold text-amber-500/70 uppercase tracking-widest mb-1">
                    Game Master
                  </div>
                )}
                <div className="text-sm leading-relaxed text-neutral-200 whitespace-pre-wrap">
                  {msg.content}
                  {isStreaming && idx === messages.length - 1 && msg.role === 'gm' && (
                    <span className="inline-block w-1.5 h-4 bg-amber-400/70 ml-0.5 animate-pulse" />
                  )}
                </div>
              </div>
            ) : (
              <div className={cn(
                'rounded-lg px-3 py-2 border',
                ACTION_TYPES.find(a => a.type === msg.actionType)?.bgColor || 'bg-white/5 border-white/10',
              )}>
                <div className={cn(
                  'text-[10px] font-bold uppercase tracking-widest mb-0.5',
                  ACTION_TYPES.find(a => a.type === msg.actionType)?.color || 'text-neutral-400',
                )}>
                  {msg.actionType?.toUpperCase() || 'ACTION'}
                </div>
                <div className="text-sm text-neutral-200">{msg.content}</div>
              </div>
            )}
          </div>
        ))}

        {/* Error message */}
        {error && !isStreaming && (
          <div className="text-xs text-red-400/80 bg-red-500/10 rounded px-3 py-2 border border-red-500/20">
            {error}
          </div>
        )}
      </div>

      {/* ── Action Mode Tabs ── */}
      <div className="px-3 pt-2 border-t border-white/10">
        <div className="flex gap-1">
          {ACTION_TYPES.map(at => {
            const Icon = at.icon;
            const isActive = actionType === at.type;
            return (
              <button
                key={at.type}
                onClick={() => {
                  setActionType(at.type);
                  inputRef.current?.focus();
                }}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-t transition-colors',
                  isActive
                    ? `${at.color} bg-white/10 border border-white/10 border-b-0`
                    : 'text-neutral-500 hover:text-neutral-300',
                )}
              >
                <Icon className="w-3 h-3" />
                {at.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Input ── */}
      <div className="px-3 pb-2">
        <div className={cn(
          'flex gap-2 rounded-b-lg rounded-tr-lg border px-3 py-2',
          'bg-neutral-900/80',
          activeAction.bgColor.replace('bg-', 'border-').replace('/15', '/20'),
        )}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={activeAction.placeholder}
            disabled={isStreaming}
            className="flex-1 bg-transparent text-sm text-white placeholder-neutral-600 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            className={cn(
              'p-1 rounded transition-colors',
              input.trim() && !isStreaming
                ? 'text-white hover:bg-white/10'
                : 'text-neutral-600',
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Next Stretch Button ── */}
      <div className="px-3 pb-3">
        <button
          onClick={onNextStretch}
          disabled={isStreaming}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
            isStreaming
              ? 'bg-neutral-800/30 text-neutral-600 border border-white/5 cursor-not-allowed'
              : 'bg-amber-600/20 text-amber-400 border border-amber-500/30 hover:bg-amber-600/30',
          )}
        >
          <ArrowRight className="w-4 h-4" />
          Continue to Next Stretch
        </button>
      </div>
    </div>
  );
}
